import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { CheckCircle2, Compass, ArrowUpRight, X } from "lucide-react";

export default function Technology() {
  const { t, lang } = useLang();
  const [fullTest, setFullTest] = useState(false);

  useEffect(() => {
    if (!fullTest) return;
    const onKey = (e) => { if (e.key === "Escape") setFullTest(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullTest]);

  return (
    <section
      id="tech"
      data-testid="section-tech"
      className="relative bg-[#0F1114] text-white py-32 md:py-48"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-14">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-8 mb-20 md:mb-24">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase text-white/45 mb-8">
                <span className="h-px w-8 bg-[#F5D77A]" />
                {t.tech.kicker}
              </div>
              <h2
                className="font-display font-light tracking-[-0.03em] leading-[1] text-white"
                style={{ fontSize: "clamp(3rem, 8vw, 7rem)" }}
              >
                {t.tech.title}
              </h2>
            </motion.div>
          </div>
          <div className="lg:col-span-4 flex items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="text-white/55 text-base leading-relaxed"
            >
              {t.tech.sub}
            </motion.p>
          </div>
        </div>

        {/* SPRINT 08 · SAUBERE TRENNUNG — 2 columns */}
        <div className="grid md:grid-cols-2 gap-0 border border-white/8">
          {/* Nachgewiesen */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            data-testid="tech-proven"
            className="relative bg-[#4F7F4C]/[0.06] border-r-0 md:border-r border-white/8 p-10 md:p-14"
          >
            <div className="flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-[#8FB588] mb-10">
              <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
              {t.tech.provenTitle}
            </div>
            <ul className="space-y-8">
              {t.tech.proven.map((p, i) => (
                <motion.li
                  key={p.t}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.15 + i * 0.1 }}
                  className="pb-8 border-b border-white/8 last:border-b-0"
                >
                  <div className="text-[10px] tracking-[0.35em] text-white/25 font-display mb-2">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div
                    className="font-display font-light tracking-[-0.02em] leading-[1] text-white"
                    style={{ fontSize: "clamp(1.5rem, 2.4vw, 2rem)" }}
                  >
                    {p.t}
                  </div>
                  <div className="mt-3 text-[14px] text-white/55 leading-relaxed max-w-md">
                    {p.d}
                  </div>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* In Entwicklung */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            data-testid="tech-dev"
            className="relative bg-[#F5D77A]/[0.04] p-10 md:p-14"
          >
            <div className="flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-[#F5D77A] mb-10">
              <Compass className="h-4 w-4" strokeWidth={2} />
              {t.tech.devTitle}
            </div>
            <ul className="space-y-8">
              {t.tech.dev.map((p, i) => (
                <motion.li
                  key={p.t}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 + i * 0.1 }}
                  className="pb-8 border-b border-white/8 last:border-b-0"
                >
                  <div className="text-[10px] tracking-[0.35em] text-white/25 font-display mb-2">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div
                    className="font-display font-light tracking-[-0.02em] leading-[1] text-white"
                    style={{ fontSize: "clamp(1.5rem, 2.4vw, 2rem)" }}
                  >
                    {p.t}
                  </div>
                  <div className="mt-3 text-[14px] text-white/55 leading-relaxed max-w-md">
                    {p.d}
                  </div>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Crashtest im Detail — verifiable fact block */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          data-testid="tech-crashtest"
          className="mt-20 md:mt-28 bg-white/[0.02] border border-white/10 p-10 md:p-16"
        >
          <div className="flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-[#F5D77A] mb-10 font-display">
            <span className="h-px w-8 bg-[#F5D77A]" />
            Der Crashtest im Detail
          </div>

          {/* Crashtest-Video · Originalaufnahme DEKRA 2018 */}
          <div className="max-w-5xl mb-12 md:mb-16" data-testid="tech-crashtest-video-block">
            <div className="relative aspect-video overflow-hidden border border-white/10 bg-black">
              <video
                ref={(el) => { if (el) el.muted = true; }}
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="/media/technologie/crashtest-poster.jpg"
                onCanPlay={(e) => { const p = e.currentTarget.play(); if (p && p.catch) p.catch(() => {}); }}
                data-testid="tech-crashtest-video"
              >
                <source src="/media/technologie/crashtest-highlight.webm" type="video/webm" />
                <source src="/media/technologie/crashtest-highlight.mp4" type="video/mp4" />
              </video>
            </div>
            <div className="mt-3 flex flex-wrap items-baseline gap-x-6 gap-y-2">
              <p className="text-[13px] leading-relaxed text-white/50">
                {lang === "de" ? "DEKRA-Crashtest 2018 — Originalaufnahme" : "DEKRA crash test 2018 — original footage"}
              </p>
              <button
                type="button"
                onClick={() => setFullTest(true)}
                data-testid="tech-crashtest-fulltest-link"
                className="text-[13px] text-[#F5D77A] hover:text-white underline underline-offset-4 decoration-[#F5D77A]/40 hover:decoration-white transition-colors"
              >
                {lang === "de" ? "Vollständigen Test ansehen (1:27)" : "Watch the full test (1:27)"}
              </button>
            </div>
          </div>

          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-5 max-w-5xl">
            {[
              { k: "Testfahrzeug",          v: "7.114 kg" },
              { k: "Aufprallgeschwindigkeit", v: "63,9 km/h" },
              { k: "Eindringtiefe",         v: "7,95 m" },
              { k: "DEKRA-Prüfbericht",     v: "Nr. 201844624" },
              { k: "Norm",                   v: "IWA 14-1:2013" },
              { k: "EU-Patent",              v: "EP 3 486 374" },
            ].map((row, i) => (
              <div
                key={row.k}
                className="grid grid-cols-12 gap-4 items-baseline py-3 border-b border-white/10"
                data-testid={`tech-crashtest-row-${i}`}
              >
                <dt className="col-span-5 md:col-span-5 text-[10px] tracking-[0.3em] uppercase text-white/45 font-display">
                  {row.k}
                </dt>
                <dd className="col-span-7 md:col-span-7 font-display font-light text-lg md:text-xl text-white tracking-[-0.005em]">
                  {row.v}
                </dd>
              </div>
            ))}
          </dl>

          <p className="mt-10 md:mt-14 max-w-3xl font-display font-light tracking-[-0.005em] leading-[1.5] text-white/75 text-base md:text-lg">
            7,95 Meter hinter dem Modul beginnt der sichere Bereich — genau so wird bei der Planung der Abstand zum geschützten Platz gewählt.
          </p>

          <a
            href="#contact"
            data-testid="tech-crashtest-report-link"
            className="group mt-8 inline-flex items-center gap-3 text-[11px] tracking-[0.35em] uppercase font-display text-[#F5D77A] hover:text-white transition-colors border-b border-[#F5D77A]/40 hover:border-white pb-1.5"
          >
            Vollständiger Prüfbericht auf Anfrage
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
          </a>
        </motion.div>

        {/* Fahrplan zur Beschaffungsreife — 3-column roadmap */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          data-testid="tech-roadmap"
          className="mt-20 md:mt-28"
        >
          <div className="flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-[#F5D77A] mb-12 font-display">
            <span className="h-px w-8 bg-[#F5D77A]" />
            Der Weg zur vollständigen Beschaffungsreife
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                stage: "Erreicht",
                subtitle: "Generation 1",
                accent: "text-white",
                border: "border-white/25",
                bg: "bg-white/[0.03]",
                items: [
                  "DEKRA-Crashtest IWA 14-1:2013",
                  "EU-Patent EP 3 486 374",
                  "Realisierung Kantstraße 2026",
                ],
              },
              {
                stage: "In Planung",
                subtitle: "Generation 2",
                accent: "text-[#F5D77A]",
                border: "border-[#F5D77A]/25",
                bg: "bg-[#F5D77A]/[0.04]",
                items: [
                  "Neuer DEKRA-Crashtest nach ISO 22343-1 — Terminvereinbarung läuft",
                  "Planung nach DIN SPEC 91414-2",
                  "Statik- und Standsicherheitsnachweis",
                ],
              },
              {
                stage: "Angestrebt",
                subtitle: "Produktreife",
                accent: "text-white/60",
                border: "border-white/15",
                bg: "bg-white/[0.015]",
                items: [
                  "CE-Konformität",
                  "Stadtmöbel-Prüfung DIN EN 581",
                  "Umwelt-Produktdeklaration (EPD)",
                  "Ausschreibungsfähige LV-Texte",
                ],
              },
            ].map((col, i) => (
              <div
                key={col.stage}
                data-testid={`tech-roadmap-col-${i}`}
                className={`${col.bg} border ${col.border} p-8 md:p-10 flex flex-col`}
              >
                <div className={`text-[10px] tracking-[0.4em] uppercase font-display mb-3 ${col.accent}`}>
                  {col.stage}
                </div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-display mb-8">
                  {col.subtitle}
                </div>
                <ul className="space-y-4">
                  {col.items.map((item, j) => (
                    <li
                      key={j}
                      className="flex items-start gap-3 text-[14px] md:text-[15px] leading-[1.55] text-white/80 font-display font-light"
                    >
                      <span className={`mt-[9px] h-1 w-1 rounded-full ${i === 0 ? "bg-white/70" : i === 1 ? "bg-[#F5D77A]/80" : "bg-white/35"} shrink-0`} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <p
            data-testid="tech-roadmap-closer"
            className="mt-12 md:mt-16 font-display italic tracking-[-0.01em] text-white/65 text-base md:text-lg max-w-3xl border-l-2 border-[#F5D77A]/40 pl-6"
          >
            Kein Ergebnis vor dem Test. Fakten statt Versprechen.
          </p>
        </motion.div>

        {/* Footnote — patents / substance */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-[10px] tracking-[0.35em] uppercase text-white/40 font-display"
        >
          <span>DEKRA IWA 14-1 N2A · GEN 1</span>
          <span className="text-white/15">·</span>
          <span>EU PATENT EP3486374</span>
        </motion.div>

        {/* Closing claim — mirrored from Hero */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          data-testid="technology-claim-de"
          className="mt-28 md:mt-40 max-w-4xl font-display italic tracking-[-0.02em] leading-[1.1] text-[#F5D77A]"
          style={{ fontSize: "clamp(1.75rem, 3.4vw, 3.25rem)" }}
        >
          Sicherheit muss man nicht sehen. Man soll sie erleben.
        </motion.p>
      </div>

      {/* Lightbox · vollständiger DEKRA-Test mit Ton */}
      {fullTest && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          data-testid="crashtest-lightbox"
          onClick={() => setFullTest(false)}
        >
          <button
            type="button"
            aria-label={lang === "de" ? "Schließen" : "Close"}
            onClick={() => setFullTest(false)}
            data-testid="crashtest-lightbox-close"
            className="absolute top-5 right-5 md:top-8 md:right-8 text-white/70 hover:text-white transition-colors"
          >
            <X className="h-7 w-7" strokeWidth={1.5} />
          </button>
          <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <video
              className="w-full aspect-video bg-black"
              controls
              autoPlay
              playsInline
              poster="/media/technologie/crashtest-poster.jpg"
              data-testid="crashtest-lightbox-video"
            >
              <source src="/media/technologie/crashtest-dekra.webm" type="video/webm" />
              <source src="/media/technologie/crashtest-dekra.mp4" type="video/mp4" />
            </video>
            <p className="mt-3 text-[13px] text-white/50">
              {lang === "de" ? "DEKRA-Crashtest 2018 — vollständiger Test (1:27)" : "DEKRA crash test 2018 — full test (1:27)"}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
